import { AutogenLayoutArchiveVoidedCommissions } from "./autogen/AutogenLayoutArchiveVoidedCommissions";
import {
    Button, Layout, LookupModelSearchEvent, PanelProcessLastRun, PanelProcessLastRunVariant, SlideoutDecorator,
    Snackbar, StepEvent
} from "@mcleod/components";
import { Alignment, Api, getThemeColor, ModelRow } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { BackOfficeReportPreview } from "../BackOfficeReportPreview";

export interface ArchiveVoidedCommissionsProps {
    payees: ModelRow[];
    afterArchive?: () => void;
}

export class ArchiveVoidedCommissions extends AutogenLayoutArchiveVoidedCommissions {
    private hasParametersChanged: boolean = true;
    private reportPreviewEditResults: BackOfficeReportPreview;
    private reportPreviewPostResults: BackOfficeReportPreview;
    public afterArchive: () => void;
    private processLog: PanelProcessLastRun = PanelProcessLastRun.create("Archive Voided Commissions", {
        variant: PanelProcessLastRunVariant.compact
    });

    async onLoad(): Promise<void> {
        this.initializeEditResultsPanel();
        this.initializePostResultsPanel();

        this.textboxCutoffDate.addChangeListener(() => {
            this.hasParametersChanged = true;
        });
        this.textboxPayee.addLookupModelSelectionListener(() => {
            this.hasParametersChanged = true;
        });
        this.textboxPayee.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter["off_payee.enable_commissions_pay"] = "Y";
        });
        this.buttonArchive.addClickListener(async () => {
            const isYes = await CommonDialogs.showYesNo("Would you like to proceed with the Archive?", "Archive Voided Commissions");
            if (!isYes) {
                this.stepEditList.select();
                return;
            }

            this.buttonArchive.busy = true;
            this.stepArchive.busy = true;
            const endpointPath = "lme/settlement/commissions/archive-voided-commissions-post";
            this.runReport(endpointPath, this.reportPreviewPostResults).finally(() => {
                this.reportPreviewPostResults.visible = true;
                this.buttonArchive.busy = false;
                this.buttonArchive.enabled = false;
                this.stepArchive.busy = false;
                this.afterArchive?.();
            });
        });
    }

    async runReport(endpointPath: string, reportPreview: BackOfficeReportPreview) {
        const payees = this.textboxPayee.lookupModelData?.map(lookupModelDataRow => lookupModelDataRow.get("id")).join(",");
        const response = await Api.post(endpointPath, {
            cutoff_date: this.textboxCutoffDate.getDataValue(),
            payees: payees
        });
        const data = response.data[0];
        const documents = data["documents"];
        if (documents.length === 0) {
            reportPreview.clearData();
            Snackbar.showSnackbar("No voided records to process.");
            this.buttonArchive.enabled = false;
        } else {
            reportPreview.data = documents;
            if (this.stepArchive.selected) {
                const numArchived = data["num_archived"];
                Snackbar.showSuccessSnackbar(`Voided commissions were successfully archived. (${numArchived})`);
                this.stepArchive.checked = true;
                this.processLog?.refreshLastProcessedData();
            } else if (this.stepEditList.selected) {
                this.stepEditList.checked = true;
            }
        }
    }

    async generateEditList() {
        if (this.hasParametersChanged) {
            this.stepEditList.busy = true;
            const endpointPath = "lme/settlement/commissions/archive-voided-commissions-edit";
            return this.runReport(endpointPath, this.reportPreviewEditResults).finally(() => {
                this.reportPreviewPostResults.clearData();
                this.hasParametersChanged = false;
                this.buttonArchive.enabled = true;
                this.stepEditList.busy = false;
            });
        }
    }

    private initializeEditResultsPanel() {
        if (this.reportPreviewEditResults == null) {
            this.reportPreviewEditResults = new BackOfficeReportPreview("");
            this.stepEditList.add(this.reportPreviewEditResults);
        }
    }

    private initializePostResultsPanel() {
        if (this.reportPreviewPostResults == null) {
            this.reportPreviewPostResults = new BackOfficeReportPreview("", false);
            this.stepArchive.add(this.reportPreviewPostResults);
        }
    }

    public static showSlideout(props: ArchiveVoidedCommissionsProps) {
        const layout = Layout.getLayout("lme/settlement/commissions/ArchiveVoidedCommissions") as ArchiveVoidedCommissions;
        const addlComponents = [];
        if (layout.processLog) {
            addlComponents.push(layout.processLog);
        }
        const buttonContinue = layout.createContinueButton();
        addlComponents.push(buttonContinue);
        SlideoutDecorator.decorateAndSlideIn(layout, {
            width: "60%",
            title: "Archive Voided Commissions",
            fillVerticalSpace: true,
            transitionOptions: {
                direction: Alignment.RIGHT
            },
            addlComponents: addlComponents,
            layoutLoadListeners: () => {
                if (props.payees.length > 0) {
                    const payeeIds = props.payees.map(row => row.get("id"));
                    const modelRow = new ModelRow("");
                    modelRow.set("payees", payeeIds.join(","));
                    modelRow.setLookupModelData("payees", props.payees);
                    layout.textboxPayee.displayData(modelRow, null, 0);
                }
                layout.stepperArchiveVoidedCommissions.addBeforeStepListener(async (event: StepEvent) => {
                    await layout.doBeforeStep(event, buttonContinue);
                });
                layout.afterArchive = props.afterArchive;
            }
        })
    }

    private async doBeforeStep(event: StepEvent, buttonContinue: Button) {
        if (event.newStep == this.stepEditList) {
            await this.generateEditList();
            if (this.reportPreviewEditResults.isEmpty()) {
                this.stepParameters.select();
                return;
            }
            this.stepParameters.checked = true;
        }
        buttonContinue.visible = event.newStep != this.stepArchive;
    }

    private createContinueButton(): Button {
        const buttonContinue: Button = new Button({
            id: "buttonContinue",
            caption: "Continue",
            color: "primary.reverse",
            backgroundColor: "primary",
            rowBreak: false,
            onClick: async () => {
                this.stepperArchiveVoidedCommissions.selectedIndex = this.stepperArchiveVoidedCommissions.selectedIndex + 1;
            }
        });
        buttonContinue.style.borderColor = getThemeColor("primary");
        return buttonContinue;
    }
}
